import React, { useCallback, useEffect, useState } from 'react';
import adminApi from '../../services/adminApi';
import { MediaService } from '../../services/mediaService';
import styles from './AdminEditorialPicks.module.css';

// Aluvo Picks placements on the home feed (docs/home-feed.md → Editorial).
// Admins pick an EXISTING public item; nothing is created or copied, and the
// feed re-checks the item's own visibility on every request.

const TYPES = [
    { id: 'ranking', label: 'Ranking', hint: 'Paste the public ranking link (aluvo.app/rankings/…) or its slug. Only published rankings can be featured.' },
    { id: 'movie', label: 'Movie', hint: 'Search the catalogue, or enter a TMDB id.' },
    { id: 'tv', label: 'TV series', hint: 'Search the catalogue, or enter a TMDB id.' },
    { id: 'collection', label: 'Collection', hint: 'The collection slug (aluvo.app/collections/…).' },
    { id: 'curated_list', label: 'Curated list', hint: 'The curated list slug (aluvo.app/discover/lists/…).' },
];
const TYPE_LABEL = Object.fromEntries(TYPES.map((t) => [t.id, t.label]));
const NOTE_MAX = 280;
const EMPTY_FORM = { itemType: 'ranking', reference: '', referenceLabel: '', note: '', priority: 100, isActive: true };

function TitleSearch({ kind, onPick }) {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);

    async function search(event) {
        event.preventDefault();
        if (query.trim().length < 2) return;
        setSearching(true);
        try {
            const data = await MediaService.search(query.trim());
            setResults((data?.results ?? []).filter((r) => r.kind === kind).slice(0, 8));
        } catch {
            setResults([]);
        } finally {
            setSearching(false);
        }
    }

    return (
        <div className={styles.search}>
            <div className={styles.inline}>
                <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') search(e); }}
                    placeholder={kind === 'tv' ? 'Search series…' : 'Search movies…'}
                    aria-label="Search the catalogue"
                />
                <button type="button" onClick={search} disabled={searching}>{searching ? 'Searching…' : 'Search'}</button>
            </div>
            {results.length > 0 && (
                <ul className={styles.results}>
                    {results.map((r) => (
                        <li key={`${r.kind}:${r.tmdbId}`}>
                            <button type="button" onClick={() => { onPick(r); setResults([]); }}>
                                {r.posterUrl ? <img src={r.posterUrl} alt="" /> : <span className={styles.noPoster} />}
                                <span>{r.title}{r.year ? ` (${r.year})` : ''}</span>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function PickRow({ pick, onChange, onDelete, busy }) {
    const [note, setNote] = useState(pick.note ?? '');
    const [priority, setPriority] = useState(String(pick.priority));
    const dirty = note !== (pick.note ?? '') || priority !== String(pick.priority);

    return (
        <li className={`${styles.pick} ${pick.isActive ? '' : styles.inactive}`}>
            <div className={styles.thumb}>
                {pick.preview?.imageUrl ? <img src={pick.preview.imageUrl} alt="" /> : <span>{TYPE_LABEL[pick.itemType]}</span>}
            </div>
            <div className={styles.pickBody}>
                <div className={styles.pickHead}>
                    <span className={styles.type}>{TYPE_LABEL[pick.itemType]}</span>
                    {pick.preview ? (
                        <a href={pick.preview.href} target="_blank" rel="noreferrer">{pick.preview.label}</a>
                    ) : <strong>Unavailable item</strong>}
                    {pick.preview?.sublabel && <span className={styles.muted}>{pick.preview.sublabel}</span>}
                </div>
                {!pick.eligible && (
                    <p className={styles.warning} role="status">Hidden from the feed: {pick.ineligibleReason}</p>
                )}
                <label className={styles.field}>
                    <span>Editorial note ({note.length}/{NOTE_MAX})</span>
                    <textarea value={note} maxLength={NOTE_MAX} rows={2} onChange={(e) => setNote(e.target.value)} />
                </label>
                <div className={styles.inline}>
                    <label className={styles.priority}>
                        <span>Priority</span>
                        <input type="number" value={priority} onChange={(e) => setPriority(e.target.value)} />
                    </label>
                    <button type="button" disabled={!dirty || busy} onClick={() => onChange(pick, { note, priority: Number(priority) })}>Save</button>
                    <button type="button" disabled={busy} onClick={() => onChange(pick, { isActive: !pick.isActive })}>
                        {pick.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                    <button type="button" className={styles.danger} disabled={busy} onClick={() => onDelete(pick)}>Remove</button>
                </div>
            </div>
        </li>
    );
}

export default function AdminEditorialPicks() {
    const [picks, setPicks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [form, setForm] = useState(EMPTY_FORM);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const data = await adminApi.listEditorialPicks();
            setPicks(data?.picks ?? []);
        } catch (err) {
            setError(err.message || 'Could not load Aluvo Picks.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    async function create(event) {
        event.preventDefault();
        setBusy(true);
        setError('');
        setMessage('');
        try {
            await adminApi.createEditorialPick({
                itemType: form.itemType,
                reference: form.reference,
                note: form.note,
                priority: Number(form.priority),
                isActive: form.isActive,
            });
            setForm({ ...EMPTY_FORM, itemType: form.itemType });
            setMessage('Added to Aluvo Picks.');
            await load();
        } catch (err) {
            setError(err.message || 'Could not add that item.');
        } finally {
            setBusy(false);
        }
    }

    async function change(pick, payload) {
        setBusy(true);
        setError('');
        try {
            const data = await adminApi.updateEditorialPick(pick.id, payload);
            setPicks((prev) => prev.map((p) => (p.id === pick.id ? data.pick : p)));
        } catch (err) {
            setError(err.message || 'Could not save that change.');
        } finally {
            setBusy(false);
        }
    }

    async function remove(pick) {
        if (!window.confirm(`Remove "${pick.preview?.label ?? 'this item'}" from Aluvo Picks? The item itself is not changed.`)) return;
        setBusy(true);
        setError('');
        try {
            await adminApi.deleteEditorialPick(pick.id);
            setPicks((prev) => prev.filter((p) => p.id !== pick.id));
        } catch (err) {
            setError(err.message || 'Could not remove that pick.');
        } finally {
            setBusy(false);
        }
    }

    const type = TYPES.find((t) => t.id === form.itemType);
    const isTitle = form.itemType === 'movie' || form.itemType === 'tv';
    const liveCount = picks.filter((p) => p.isActive && p.eligible).length;

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <div>
                    <h1>Aluvo Picks on Home</h1>
                    <p>
                        Feature existing public rankings, titles, collections or curated lists in members' For You feed,
                        labelled “Aluvo Picks”. Lower priority shows first. {liveCount} live now.
                    </p>
                </div>
            </header>

            {error && <div className={styles.error} role="alert">{error}</div>}
            {message && <div className={styles.message} role="status">{message}</div>}

            <form className={styles.form} onSubmit={create}>
                <h2>Add a pick</h2>
                <div className={styles.typeRow} role="radiogroup" aria-label="Item type">
                    {TYPES.map((t) => (
                        <button
                            type="button"
                            key={t.id}
                            role="radio"
                            aria-checked={form.itemType === t.id}
                            className={form.itemType === t.id ? styles.typeActive : ''}
                            onClick={() => setForm({ ...form, itemType: t.id, reference: '', referenceLabel: '' })}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
                <p className={styles.muted}>{type.hint}</p>
                {isTitle && (
                    <TitleSearch
                        kind={form.itemType}
                        onPick={(r) => setForm({ ...form, reference: String(r.tmdbId), referenceLabel: `${r.title}${r.year ? ` (${r.year})` : ''}` })}
                    />
                )}
                <label className={styles.field}>
                    <span>{isTitle ? 'TMDB id' : 'Link or slug'}</span>
                    <input
                        value={form.reference}
                        onChange={(e) => setForm({ ...form, reference: e.target.value, referenceLabel: '' })}
                        required
                    />
                    {form.referenceLabel && <small className={styles.muted}>Selected: {form.referenceLabel}</small>}
                </label>
                <label className={styles.field}>
                    <span>Editorial note ({form.note.length}/{NOTE_MAX}) — why this is worth a look</span>
                    <textarea value={form.note} maxLength={NOTE_MAX} rows={2} onChange={(e) => setForm({ ...form, note: e.target.value })} />
                </label>
                <div className={styles.inline}>
                    <label className={styles.priority}>
                        <span>Priority</span>
                        <input type="number" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} />
                    </label>
                    <label className={styles.check}>
                        <input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
                        <span>Active</span>
                    </label>
                    <button type="submit" disabled={busy || !form.reference.trim()}>{busy ? 'Saving…' : 'Add to Aluvo Picks'}</button>
                </div>
            </form>

            <section>
                <h2>Current picks</h2>
                {loading && <p className={styles.muted}>Loading…</p>}
                {!loading && picks.length === 0 && (
                    <p className={styles.muted}>
                        No picks yet. The feed works without them: members see rankings, recommendations and Aluvo Picks
                        curated lists instead.
                    </p>
                )}
                <ul className={styles.list}>
                    {picks.map((pick) => (
                        <PickRow key={`${pick.id}:${pick.updatedAt}`} pick={pick} onChange={change} onDelete={remove} busy={busy} />
                    ))}
                </ul>
            </section>
        </div>
    );
}
